import { useMemo } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const kpis = [
  { label: "Daily Orders", value: 312, status: "good" },
  { label: "Avg Prep Time", value: "18 mins", status: "good" },
  { label: "Kitchen Efficiency", value: "92%", status: "excellent" },
  { label: "Inventory Health", value: "Good", status: "good" },
];

const hourlyOrders = [
  { hour: "10 AM", orders: 45 },
  { hour: "12 PM", orders: 78 },
  { hour: "2 PM", orders: 65 },
  { hour: "4 PM", orders: 52 },
  { hour: "6 PM", orders: 110 },
  { hour: "8 PM", orders: 130 },
];

const prepTimeTrend = [
  { day: "Mon", time: 22 },
  { day: "Tue", time: 20 },
  { day: "Wed", time: 19 },
  { day: "Thu", time: 18 },
  { day: "Fri", time: 17 },
  { day: "Sat", time: 16 },
];

export default function OperationalKPIs() {
  const bottleneckHour = useMemo(
    () =>
      hourlyOrders.reduce((a, b) =>
        b.orders > a.orders ? b : a
      ).hour,
    []
  );

  return (
    <DashboardLayout>
      <div className="space-y-12">

        <div>
          <h1 className="text-3xl font-serif">Operational KPIs</h1>
          <p className="text-sm text-gray-400 mt-1">
            Kitchen · Orders · Efficiency · Inventory
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">
              Orders by Hour
            </h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hourlyOrders}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="orders"
                  fill="#f59e0b"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <p className="mt-3 text-xs text-gray-400">
              Peak load observed at <span className="text-amber-400">{bottleneckHour}</span>
            </p>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">
              Avg Preparation Time Trend
            </h2>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={prepTimeTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="time"
                  stroke="#22c55e"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="mt-3 text-xs text-gray-400">
              Continuous improvement in prep efficiency
            </p>
          </div>

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <InsightCard
            title="Operational Bottlenecks"
            items={[
              "High order congestion during evening peak",
              "Dessert section load spikes after 7 PM",
              "Packaging delays during weekends",
            ]}
          />

          <InsightCard
            title="SLA & Performance"
            items={[
              "92% orders within SLA",
              "Avg delay reduced by 8%",
              "Kitchen response time stable",
            ]}
          />

          <InsightCard
            title="Inventory & Waste"
            items={[
              "Stock usage within optimal range",
              "Low wastage reported this week",
              "Fast-moving items restocked timely",
            ]}
          />

        </div>

      </div>
    </DashboardLayout>
  );
}

function KpiCard({ label, value, status }) {
  const statusColor =
    status === "excellent"
      ? "text-emerald-400"
      : status === "good"
      ? "text-amber-400"
      : "text-rose-400";

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs tracking-widest uppercase text-gray-400">
        {label}
      </p>
      <p className={`mt-3 text-2xl font-serif ${statusColor}`}>
        {value}
      </p>
    </div>
  );
}

function InsightCard({ title, items }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <h3 className="font-serif mb-3">{title}</h3>
      <ul className="text-sm text-gray-400 space-y-2">
        {items.map((i, idx) => (
          <li key={idx}>• {i}</li>
        ))}
      </ul>
    </div>
  );
}
